// Ninocat fleet-routing prefix parser — the client-side mirror of
// internal/bulksql/parse.go. Used by the Fleet Query panel to dispatch
// each statement to its target connection BEFORE sending it to the server,
// so the message log can stream per-statement updates (the Bulk SQL Runner
// modal uses /api/tools/bulk-sql/run which is batch — not what we want
// here).
//
// Syntax:
//   UPDATE [Bayiku Surodinawan].dbbayiku.tuser SET x=1 WHERE ...
//
// `[Bayiku Surodinawan]` is NOT MySQL syntax — MySQL uses backticks. It's
// a Ninocat-only routing prefix, modelled on SQL Server's four-part
// `[server].[db].[schema].[table]` linked-server naming.
//
// parseStatement(sql) → { conn, rewritten }
//   conn       — the target connection name, or '' if none (caller picks default)
//   rewritten  — sql with every `[name].` prefix removed (valid MySQL)
//
// Throws on:
//   - unterminated `[`
//   - empty `[]`
//   - two different connections in ONE statement (no cross-server joins)
//
// The same-statement dedup is case-insensitive (matches Go's strings.EqualFold
// for the ASCII connection names we accept).
//
// Quote/comment/backtick aware: `[` inside '…' / "…" / `…` / -- / # / /*…*/
// is literal and left alone.

export function parseStatement(sql) {
  let out = '';
  const names = [];
  const n = sql.length;
  let i = 0;
  while (i < n) {
    const c = sql[i];
    if (c === "'" || c === '"' || c === '`') {
      const j = skipQuoted(sql, i);
      out += sql.slice(i, j);
      i = j;
    } else if (c === '-' && i + 1 < n && sql[i + 1] === '-') {
      const j = skipToNewline(sql, i);
      out += sql.slice(i, j);
      i = j;
    } else if (c === '#') {
      const j = skipToNewline(sql, i);
      out += sql.slice(i, j);
      i = j;
    } else if (c === '/' && i + 1 < n && sql[i + 1] === '*') {
      const j = skipBlockComment(sql, i);
      out += sql.slice(i, j);
      i = j;
    } else if (c === '[') {
      const closeRel = sql.indexOf(']', i + 1);
      if (closeRel < 0) throw new Error('unterminated [connection] reference');
      const name = sql.slice(i + 1, closeRel).trim();
      if (name === '') throw new Error('empty [connection] reference');
      names.push(name);
      i = closeRel + 1;
      if (i < n && sql[i] === '.') i++; // strip the joining dot if present
    } else {
      out += c;
      i++;
    }
  }

  let conn = '';
  for (const nm of names) {
    if (conn === '') { conn = nm; continue; }
    if (nm.toLowerCase() !== conn.toLowerCase()) {
      throw new Error(`statement references two connections (${JSON.stringify(conn)} and ${JSON.stringify(nm)}) — a single statement can't run across servers`);
    }
  }
  return { conn, rewritten: out };
}

// '…' / "…" / `…` — handles backslash escapes (except inside backticks, where
// `\` is literal) and doubled-quote escaping ('' "" ``).
function skipQuoted(s, i) {
  const q = s[i];
  const n = s.length;
  let j = i + 1;
  while (j < n) {
    const c = s[j];
    if (c === '\\' && q !== '`' && j + 1 < n) { j += 2; continue; }
    if (c === q) {
      if (j + 1 < n && s[j + 1] === q) { j += 2; continue; } // doubled → literal
      return j + 1;
    }
    j++;
  }
  return n;
}

function skipToNewline(s, i) {
  const idx = s.indexOf('\n', i);
  return idx >= 0 ? idx + 1 : s.length;
}

function skipBlockComment(s, i) {
  const idx = s.indexOf('*/', i + 2);
  return idx >= 0 ? idx + 2 : s.length;
}
